import React, { useState, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import Dialog from '../../../components/ui/dialog';
import TextInput from '../../../components/ui/textInput';
import { callAPI, validatePassword } from '../../../components/utils';
import Alert from '../../../components/ui/alert';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import TwoFactorAuth from '../../../components/security/twoFactorAuth';

const ChangePasswordForm = ({ open, onClose, onSuccess, otpEnabled }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifyWithOtp, setVerifyWithOtp] = useState(false);
  // const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Resets values when pop up is closed
  useEffect(() => {
    if (!open) {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setVerifyWithOtp(false);
      setErrorMessage('');
    }
  }, [open]);

  const handlePasswordChange = async () => {
    const token = localStorage.getItem("authToken");

    try {
      await callAPI({
        request: "changePassword",
        method: "post",
        params: { oldPassword, newPassword, token },
        onSuccess: () => {
          // setMessage("Password changed successfully.");
          onSuccess();
        },
        onError: (error) => {
          setErrorMessage("Change password failed: " + error.response?.data?.errorMessage);
        },
      });
    } catch (error) {
      console.error("Error changing password:", error);
      setErrorMessage("An error occurred.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = validatePassword(newPassword);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setErrorMessage('Please fill in all the fields.');
      return;
    }

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    if (otpEnabled) {
      setVerifyWithOtp(true);
    } else {
      handlePasswordChange();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={verifyWithOtp ? 'Authentication' : 'Change Password'}
      description={
        verifyWithOtp
          ? 'Enter the code from your authenticator app'
          : 'Your password must be at least 8 characters and include uppercase and lowercase letters, numbers, and special characters.'
      }
      onCancel={onClose}
      onConfirm={handleSubmit}
      confirmText={'Change Password'}
      hideCancel={verifyWithOtp}
      hideConfirm={verifyWithOtp}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {verifyWithOtp ? (
          <TwoFactorAuth
            password={oldPassword}
            onSuccess={handlePasswordChange}
          /> //need email for this
        ) : (
          <>
            <Stack spacing={2.5}>
              <TextInput
                label="Current Password"
                placeholder="Enter your current password"
                type={showOldPassword ? 'text' : 'password'}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                trailingIcon={
                  showOldPassword ? <Visibility /> : <VisibilityOff />
                }
                onIconClick={() => setShowOldPassword(!showOldPassword)}
                required
              />

              <TextInput
                label="New Password"
                placeholder="Enter your new password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                trailingIcon={
                  showNewPassword ? <Visibility /> : <VisibilityOff />
                }
                onIconClick={() => setShowNewPassword(!showNewPassword)}
                required
              />

              <TextInput
                label="Confirm New Password"
                placeholder="Enter your new password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                trailingIcon={
                  showConfirmPassword ? <Visibility /> : <VisibilityOff />
                }
                onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                required
              />
              {errorMessage && (
                <Alert
                  type="error"
                  message={errorMessage}
                  onClose={() => setErrorMessage('')}
                />
              )}
            </Stack>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default ChangePasswordForm;
